import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Sparkles, RefreshCw, AlertTriangle, CheckCircle2, Clock,
  ShieldAlert, Printer, ChevronRight, FileText,
} from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';
import { useClinic, buildPrintHeaderHTML } from '../context/ClinicContext';

// ═══════════════════════════════════════════════════════════════════
//  AuditPrepPage — AI-generated punch list of what to fix BEFORE the
//  NABH auditor visits. Pulls from /api/nabh/audit-prep which feeds
//  90-day stats + KPIs + open incidents/grievances + missing kinds
//  to Claude and gets back a prioritized action list.
// ═══════════════════════════════════════════════════════════════════

const PRIORITY_STYLE = {
  critical: { bg: '#fee2e2', border: '#dc2626', txt: '#7f1d1d', label: '🚨 CRITICAL', icon: <ShieldAlert size={14}/> },
  high:     { bg: '#fef3c7', border: '#ca8a04', txt: '#854d0e', label: '⚠️ HIGH',     icon: <AlertTriangle size={14}/> },
  medium:   { bg: '#dbeafe', border: '#2563eb', txt: '#1e40af', label: '📌 MEDIUM',   icon: <Clock size={14}/> },
  low:      { bg: '#f3f4f6', border: '#6b7280', txt: '#374151', label: '💡 LOW',      icon: <CheckCircle2 size={14}/> },
};

const READINESS = {
  ready:        { bg: '#dcfce7', txt: '#15803d', label: '✓ AUDIT-READY',   message: 'Clinic looks good. Final touch-ups only.' },
  'needs-work': { bg: '#fef3c7', txt: '#a16207', label: '⚠ NEEDS WORK',     message: 'Several gaps to close before the visit.' },
  'at-risk':    { bg: '#fee2e2', txt: '#b91c1c', label: '🚨 AT RISK',        message: 'Significant compliance gaps — urgent action needed.' },
};

const DEADLINE_LABEL = { today: 'Today', 'this-week': 'This week', 'this-month': 'This month' };

export default function AuditPrepPage({ API_BASE, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const { clinic } = useClinic();

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API_BASE}/nabh/audit-prep`);
      if (r.data?.available === false) {
        setErr(`AI not configured: ${r.data.reason || 'set ANTHROPIC_API_KEY on server'}`);
      } else {
        setData(r.data);
      }
    } catch (e) { setErr('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { /* don't auto-run — costs an AI call. User clicks "Generate". */ }, []);

  const printChecklist = () => {
    if (!data) return;
    const header = buildPrintHeaderHTML(clinic, 'plain');
    const itemsHtml = (data.items || []).map((i, idx) => {
      const p = PRIORITY_STYLE[i.priority] || PRIORITY_STYLE.low;
      return `<tr>
        <td style="text-align:center;font-weight:700;">${idx + 1}</td>
        <td><span style="background:${p.bg};color:${p.txt};padding:2px 7px;border-radius:9px;font-size:9px;font-weight:700;">${escapeHtml(p.label)}</span></td>
        <td><strong>${escapeHtml(i.title || '')}</strong><br/><span style="color:#555;font-size:10px;">${escapeHtml(i.issue || '')}</span></td>
        <td>${escapeHtml(i.action || '')}</td>
        <td style="text-align:center;font-size:10px;">${escapeHtml(DEADLINE_LABEL[i.deadline] || i.deadline || '')}</td>
        <td style="text-align:center;width:60px;"><div style="border:1px solid #999;height:14px;"></div></td>
      </tr>`;
    }).join('');
    const w = window.open('', '_blank', 'width=900,height=900');
    w.document.write(`<!doctype html><html><head><title>NABH Audit Prep — ${clinic?.clinicName || 'Clinic'}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; font-size: 11px; line-height: 1.4; }
  table { width:100%; border-collapse:collapse; margin-top: 14px; }
  th { background:#0b1a2b; color:white; padding:6px 8px; font-size:10px; text-transform:uppercase; text-align:left; }
  td { border-bottom:1px solid #e3e6ea; padding:7px 8px; font-size:10.5px; vertical-align:top; }
  tr:nth-child(even) td { background:#fafbfc; }
  .summary { padding:14px; border-radius:8px; margin:14px 0; background:#f1f5f9; }
</style></head><body>
${header}
<h2 style="font-size:18px;letter-spacing:1px;text-transform:uppercase;color:#0b1a2b;">NABH Pre-Audit Action Checklist</h2>
<div style="font-size:11px;color:#555;">Generated ${new Date().toLocaleString('en-IN')} · 90-day analysis · ${data.items?.length || 0} action items</div>
<div class="summary"><strong>Overall Readiness:</strong> ${escapeHtml((READINESS[data.overallReadiness] || {}).label || data.overallReadiness)}<br/>${escapeHtml(data.summary || '')}</div>
<table><thead><tr>
  <th style="width:30px;">#</th>
  <th style="width:80px;">Priority</th>
  <th>Issue</th>
  <th>Action</th>
  <th style="width:80px;">Deadline</th>
  <th style="width:50px;">✓</th>
</tr></thead><tbody>${itemsHtml || '<tr><td colspan="6" style="text-align:center;padding:30px;">No items.</td></tr>'}</tbody></table>
<div style="margin-top:36px;display:flex;justify-content:space-between;font-size:10.5px;">
  <div style="border-top:1px solid #333;padding-top:4px;width:180px;text-align:center;">Quality Manager</div>
  <div style="border-top:1px solid #333;padding-top:4px;width:180px;text-align:center;">Director / Doctor</div>
  <div style="border-top:1px solid #333;padding-top:4px;width:180px;text-align:center;">Date &amp; Stamp</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},200);};</script>
</body></html>`);
    w.document.close();
  };

  const readiness = data ? (READINESS[data.overallReadiness] || READINESS['needs-work']) : null;
  const grouped = (data?.items || []).reduce((acc, item) => {
    const p = item.priority || 'low';
    acc[p] = acc[p] || [];
    acc[p].push(item);
    return acc;
  }, {});

  return (
    <div>
      <div style={{
        ...cardStyle, marginBottom: 14,
        background: `linear-gradient(135deg, #4f46e5, #7c3aed)`, color: 'white', border: 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Sparkles size={32} />
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.35rem' }}>AI Audit Prep</h2>
            <p style={{ margin: '2px 0 0', opacity: 0.9, fontSize: 13 }}>
              "What needs attention before NABH visit?" — 90-day AI analysis with prioritized action list
            </p>
          </div>
          <button onClick={load} disabled={loading}
            style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.95)', color: '#4f46e5', border: 'none', fontWeight: 800 }}>
            {loading ? <><RefreshCw size={14} className="spin"/> <span style={{ marginLeft: 6 }}>Analyzing...</span></>
                     : <><Sparkles size={14}/> <span style={{ marginLeft: 6 }}>{data ? 'Re-analyze' : 'Generate Punch List'}</span></>}
          </button>
          {data && (
            <button onClick={printChecklist} style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.4)' }}>
              <Printer size={14}/> <span style={{ marginLeft: 6 }}>Print</span>
            </button>
          )}
        </div>
      </div>

      {err && (
        <div style={{ ...cardStyle, marginBottom: 14, background: '#fee2e2', borderColor: theme.danger, color: theme.danger }}>
          ⚠ {err}
        </div>
      )}

      {!data && !loading && !err && (
        <div style={{ ...cardStyle, padding: 30, textAlign: 'center' }}>
          <Sparkles size={40} color={theme.primary} style={{ opacity: 0.4 }}/>
          <h3 style={{ margin: '10px 0 6px', color: theme.navy }}>Ready to prep your audit?</h3>
          <p style={{ color: theme.textMuted, fontSize: 13, marginBottom: 16 }}>
            AI will analyze your last 90 days and tell you exactly what to fix.
          </p>
          <button onClick={load} style={{ ...btnStyle, marginTop: 0, background: theme.primary, color: 'white' }}>
            <Sparkles size={14}/> <span style={{ marginLeft: 6 }}>Generate Punch List</span>
          </button>
        </div>
      )}

      {loading && (
        <div style={{ ...cardStyle, padding: 30, textAlign: 'center' }}>
          <RefreshCw size={32} color={theme.primary} className="spin"/>
          <p style={{ color: theme.textMuted, marginTop: 10 }}>AI is analyzing 90 days of compliance data...</p>
        </div>
      )}

      {data && (
        <>
          {/* READINESS BADGE */}
          {readiness && (
            <div style={{
              ...cardStyle, marginBottom: 14, padding: 16,
              background: readiness.bg, borderLeft: `5px solid ${readiness.txt}`,
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: readiness.txt }}>{readiness.label}</div>
              <div style={{ fontSize: 13, color: readiness.txt, marginTop: 4 }}>{data.summary || readiness.message}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
                Generated {new Date(data.generatedAt).toLocaleString('en-IN')} · {data.items?.length || 0} action items
              </div>
            </div>
          )}

          {/* PRIORITY GROUPS */}
          {['critical', 'high', 'medium', 'low'].map(prio => {
            const items = grouped[prio];
            if (!items || items.length === 0) return null;
            const ps = PRIORITY_STYLE[prio];
            return (
              <div key={prio} style={{ marginBottom: 14 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  background: ps.bg, borderRadius: '8px 8px 0 0',
                  border: `1px solid ${ps.border}33`, borderBottom: 'none',
                }}>
                  {ps.icon}
                  <strong style={{ color: ps.txt, fontSize: 13, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                    {ps.label} — {items.length} item{items.length === 1 ? '' : 's'}
                  </strong>
                </div>
                <div style={{ ...cardStyle, padding: 0, borderRadius: '0 0 8px 8px', borderTop: 'none' }}>
                  {items.map((item, i) => (
                    <div key={i} style={{
                      padding: '12px 14px',
                      borderBottom: i < items.length - 1 ? `1px solid ${theme.borderLight}` : 'none',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: theme.navy }}>{item.title}</div>
                          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>{item.issue}</div>
                          <div style={{ fontSize: 12.5, color: theme.navy, marginTop: 6, padding: '6px 10px', background: theme.bg, borderRadius: 6, borderLeft: `3px solid ${theme.primary}` }}>
                            <strong>Action:</strong> {item.action}
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {item.category && (
                              <span style={{ fontSize: 10.5, fontWeight: 700, color: theme.textMuted, background: theme.borderLight, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>
                                {item.category}
                              </span>
                            )}
                            {item.deadline && (
                              <span style={{ fontSize: 10.5, fontWeight: 700, color: ps.txt, background: ps.bg, padding: '2px 6px', borderRadius: 4 }}>
                                ⏰ {DEADLINE_LABEL[item.deadline] || item.deadline}
                              </span>
                            )}
                            {item.kind && onNavigate && (
                              <button onClick={() => onNavigate('registers', { kind: item.kind })}
                                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: theme.primary, fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                Open {item.kind} <ChevronRight size={13}/>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
